import { INGESTION_LABEL_MAX, INGESTION_SOURCE_TYPES, type IngestionSourceType, type NewIngestionKeyResult } from '@jums-dashboard/shared';
import { useEffect, useId, useMemo, useState } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';

import useCreateIngestionKey from '../hooks/useCreateIngestionKey';
import { copyToClipboard, formatDateMs } from '../lib/format';
import { deriveSourceId, isValidLabel, isValidSourceId } from '../types/ingestion';
import ReadOnlyRow from './ReadOnlyRow';

interface Props {
  open: boolean;
  homeId: string;
  onClose: () => void;
  /** SourceIds already in use by ENABLED keys; the new name must not collide. */
  takenSourceIds?: string[];
  /** When provided, opens directly into the reveal state for a freshly minted key. */
  initialResult?: NewIngestionKeyResult | null;
}

export default function NewIngestionKeyModal({ open, homeId, onClose, takenSourceIds = [], initialResult = null }: Props) {
  const create = useCreateIngestionKey();

  const [source, setSource] = useState<IngestionSourceType>('ruuvi-station');
  const [name, setName] = useState('');
  const [result, setResult] = useState<NewIngestionKeyResult | null>(initialResult);

  const nameId = useId();

  useEffect(() => {
    if (!open) {
      setSource('ruuvi-station');
      setName('');
      setResult(null);
      create.reset();
    } else if (initialResult) {
      setResult(initialResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialResult]);

  const trimmedName = name.trim();
  const derivedId = useMemo(() => deriveSourceId(trimmedName), [trimmedName]);
  const idTaken = derivedId !== '' && takenSourceIds.includes(derivedId);
  const labelValid = isValidLabel(trimmedName);
  const idValid = isValidSourceId(derivedId);
  const canSubmit = labelValid && idValid && !idTaken && !create.isPending;

  if (result) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        manageHistory={false}
        title="Ingestion key created"
        description="This is the only time the secret URL is shown. Copy it now."
        size="md"
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          <div className="rounded-md border border-(--accent-warning) bg-(--accent-warning)/10 px-3 py-2 text-sm text-(--text-primary)">
            The full URL contains the secret key. It will not be shown again — copy it now and configure your device.
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-(--text-secondary)">Ingestion URL</label>
            <code className="block w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-xs break-all text-(--text-primary)">
              {result.ingestUrl}
            </code>
            <div className="flex justify-center pt-1">
              <Button size="sm" variant="secondary" onClick={() => void copyToClipboard(result.ingestUrl, 'Ingest URL')}>
                Copy URL
              </Button>
            </div>
          </div>
          <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) px-3 py-2">
            <ReadOnlyRow label="Name" value={result.label} />
            <ReadOnlyRow label="Source" value={result.source} />
            <ReadOnlyRow label="Source id" value={result.sourceId} mono copyLabel="source id" />
            <ReadOnlyRow label="Created" value={formatDateMs(result.createdAtMs)} />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      manageHistory={false}
      title="New ingestion key"
      description="Generate a single-use URL for a sensor source. You can disable it later."
      size="md"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          create.mutate({ homeId, source, sourceId: derivedId, label: trimmedName }, { onSuccess: (data) => setResult(data) });
        }}
      >
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-(--text-secondary)">Source type</label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as IngestionSourceType)}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          >
            {INGESTION_SOURCE_TYPES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor={nameId} className="text-xs font-medium text-(--text-secondary)">
            Source name
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={INGESTION_LABEL_MAX + 10}
            placeholder="Wall tablet (Ruuvi Station)"
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          <p className="text-xs text-(--text-tertiary)">
            {idTaken ? (
              <span className="text-(--accent-danger)">A key with this name is already active.</span>
            ) : derivedId ? (
              <>
                Saved as <code className="font-mono">{derivedId}</code>
              </>
            ) : (
              'Use a short, descriptive name.'
            )}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!canSubmit}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
